import { ItemBar, ItemBarLeft } from "components/ItemBar";
import { LoadingSpinner } from "components/LoadingSpinner";
import { ToastContext } from "context/ToastContext";
import { sizer } from "layout/styles/styled/sizer";
import { debounce } from "lodash";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Paginator, PaginatorPageState } from "primereact/paginator";
import { Tooltip } from "primereact/tooltip";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  useGetHstSyncRecordsQuery,
  useGetHstSyncSettingsQuery,
  useGetHstSyncStatsQuery,
  useUpdateHstSyncSettingsMutation,
  useUpdateHstSyncStatusMutation,
} from "store/queries/hstSync";
import styled from "styled-components";
import {
  HstSyncStatus,
  HstSyncType,
  IHstSyncRecord,
  IHstSyncStats,
} from "types/HstSync";

const MonospaceInput = styled.div`
  .p-inputtext {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  }
`;

const HospitalRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizer(2)};
  flex-wrap: wrap;
  margin-bottom: ${sizer(3)};
`;

const StatsCards = styled.div`
  display: flex;
  gap: ${sizer(1)};
`;

const StatCard = styled.div<{
  variant?: string;
  clickable?: boolean;
  active?: boolean;
}>`
  min-width: 72px;
  padding: ${sizer(0.5)} ${sizer(1.5)};
  border-radius: 8px;
  text-align: center;
  border: 1px solid var(--surface-border);
  background-color: ${({ variant }) => {
    switch (variant) {
      case "pending":
        return "var(--yellow-50)";
      case "inProgress":
        return "var(--blue-50)";
      case "synced":
        return "var(--green-50)";
      case "failed":
        return "var(--red-50)";
      case "skipped":
        return "var(--surface-ground)";
      default:
        return "var(--surface-card)";
    }
  }};
  cursor: ${({ clickable }) => (clickable ? "pointer" : "default")};
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  ${({ active }) =>
    active &&
    `
    outline: 2px solid var(--primary-color);
    outline-offset: -2px;
  `}

  &:hover {
    ${({ clickable }) =>
      clickable &&
      `
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    `}
  }

  .stat-count {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    color: ${({ variant }) => {
      switch (variant) {
        case "pending":
          return "var(--yellow-700)";
        case "inProgress":
          return "var(--blue-700)";
        case "synced":
          return "var(--green-700)";
        case "failed":
          return "var(--red-700)";
        case "skipped":
          return "var(--text-color-secondary)";
        default:
          return "var(--text-color)";
      }
    }};
  }

  .stat-label {
    font-size: 11px;
    font-weight: 500;
    color: var(--text-color-secondary);
    margin-top: 0;
  }
`;

const StyledDataTable = styled(DataTable)`
  .p-datatable-tbody > tr > td {
    padding: ${sizer(2)} ${sizer(3)};
  }
`;

const StatusBadge = styled(Badge)<{ status: HstSyncStatus }>`
  && {
    background-color: ${({ status }) => {
      switch (status) {
        case "PENDING":
          return "var(--yellow-500)";
        case "IN_PROGRESS":
          return "var(--blue-500)";
        case "SYNCED":
          return "var(--green-500)";
        case "FAILED":
          return "var(--red-500)";
        case "SKIPPED":
          return "var(--gray-500)";
        default:
          return "var(--gray-500)";
      }
    }};
  }
`;

const MutedSmall = styled.span`
  font-size: 11px;
  color: var(--text-color-secondary);
  white-space: nowrap;
`;

const TypeText = styled.span`
  white-space: nowrap;
`;

const FailedAtText = styled.span`
  color: var(--red-600);

  .failed-label {
    font-size: 12px;
    font-weight: 600;
  }
`;

const ErrorText = styled.span`
  color: var(--red-600);
  font-size: 12px;
  max-width: 200px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
`;

const NoteText = styled.span`
  font-size: 12px;
  max-width: 180px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
`;

// One source of truth for how a sync type reads: the Type column and the Type filter both use it.
const TYPE_LABELS: Record<HstSyncType, string> = {
  ESTIMATE: "Estimate",
  PAYMENT: "Payment",
  REDONE: "Redone",
  ESTIMATE_OPENED_NOTE: "Estimate Opened Note",
  MANUAL_NOTE_ADDED: "Manual Note Added",
};

const typeOptions = [
  { label: "All Types", value: null },
  ...(Object.entries(TYPE_LABELS) as [HstSyncType, string][]).map(
    ([value, label]) => ({ label, value })
  ),
];

interface IStatCardDefinition {
  key: keyof IHstSyncStats;
  label: string;
  // The status the card filters by; null is the Total card, which clears the filter.
  status: HstSyncStatus | null;
  variant?: string;
}

const STAT_CARDS: IStatCardDefinition[] = [
  { key: "total", label: "Total", status: null },
  { key: "pending", label: "Pending", status: "PENDING", variant: "pending" },
  {
    key: "inProgress",
    label: "In Progress",
    status: "IN_PROGRESS",
    variant: "inProgress",
  },
  { key: "synced", label: "Synced", status: "SYNCED", variant: "synced" },
  { key: "failed", label: "Failed", status: "FAILED", variant: "failed" },
  { key: "skipped", label: "Skipped", status: "SKIPPED", variant: "skipped" },
];

const PAGE_SIZE = 20;

const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

// The first UUID anywhere in the text, so a pasted `/orders/<uuid>` link works as well as a bare ID.
const extractEstimateId = (text: string): string | null => {
  const match = text.match(UUID_PATTERN);
  return match ? match[0].toLowerCase() : null;
};

// The HST payment-sync admin view: the RPA robot's per-account settings, its sync queue stats
// and the records table. Was the standalone `/hst-sync` page until CLE-2877 moved it under
// Integrations; the page title is set once by Integrations.
export function HstPaymentSyncTab() {
  const toastRef = useContext(ToastContext);
  const [first, setFirst] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState<HstSyncStatus | null>(null);
  const [typeFilter, setTypeFilter] = useState<HstSyncType | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null
  );
  const [allowedIpInput, setAllowedIpInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearch(value);
      setFirst(0);
    }, 400),
    []
  );

  useEffect(() => {
    debouncedSetSearch(searchInput);
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchInput, debouncedSetSearch]);

  // One box searches both ways: text holding an estimate ID (or its link) looks the estimate up,
  // anything else is a patient name.
  const estimateIdFilter = extractEstimateId(debouncedSearch);

  const clearSearch = () => {
    debouncedSetSearch.cancel();
    setSearchInput("");
    setDebouncedSearch("");
    setFirst(0);
  };

  const {
    data: statsData,
    isLoading: statsLoading,
    isFetching: statsFetching,
    refetch: refetchStats,
  } = useGetHstSyncStatsQuery({
    accountId: selectedAccountId ?? undefined,
  });

  const {
    data: recordsData,
    isLoading: recordsLoading,
    isFetching: recordsFetching,
    refetch: refetchRecords,
  } = useGetHstSyncRecordsQuery({
    // An estimate-ID search looks across every hospital, so the row is found wherever it belongs.
    accountId: estimateIdFilter ? undefined : selectedAccountId ?? undefined,
    orderId: estimateIdFilter ?? undefined,
    patientName: estimateIdFilter
      ? undefined
      : debouncedSearch.trim() || undefined,
    status: statusFilter || undefined,
    syncType: typeFilter || undefined,
    page: first / PAGE_SIZE,
    pageSize: PAGE_SIZE,
  });

  const [updateSyncStatus] = useUpdateHstSyncStatusMutation();

  const {
    data: settingsData,
    isLoading: settingsLoading,
    refetch: refetchSettings,
  } = useGetHstSyncSettingsQuery();
  const [updateHstSyncSettings] = useUpdateHstSyncSettingsMutation();

  const integrations = settingsData?.integrations ?? [];

  useEffect(() => {
    if (integrations.length > 0 && !selectedAccountId) {
      setSelectedAccountId(integrations[0].accountId);
    }
  }, [integrations, selectedAccountId]);

  const selectedIntegration = useMemo(
    () => integrations.find((i) => i.accountId === selectedAccountId) ?? null,
    [integrations, selectedAccountId]
  );

  const hospitalOptions = useMemo(
    () =>
      integrations.map((i) => ({
        label: i.accountName,
        value: i.accountId,
      })),
    [integrations]
  );

  const handleHospitalChange = (accountId: string) => {
    setSelectedAccountId(accountId);
    setFirst(0);
  };

  useEffect(() => {
    setAllowedIpInput(selectedIntegration?.allowedIp ?? "");
  }, [selectedIntegration?.allowedIp, selectedIntegration?.accountId]);

  const robotActive = selectedIntegration?.active ?? false;

  const showToast = (severity: "success" | "error", summary: string) => {
    toastRef?.current?.show({
      severity,
      summary,
      life: severity === "error" ? 6000 : 3000,
    });
  };

  const handleRobotToggle = async (active: boolean) => {
    if (!selectedIntegration) return;
    const { accountId, accountName } = selectedIntegration;
    try {
      await updateHstSyncSettings({ accountId, active }).unwrap();
      refetchSettings();
      showToast(
        "success",
        `Robot ${active ? "enabled" : "disabled"} for ${accountName}`
      );
    } catch (error) {
      console.error("Failed to update HST robot settings:", error);
      showToast("error", "Could not update the robot setting");
    }
  };

  const handleAllowedIpBlur = async () => {
    if (!selectedAccountId) return;
    const newValue = allowedIpInput.trim() || null;
    const currentValue = selectedIntegration?.allowedIp ?? null;
    if (newValue === currentValue) return;
    try {
      await updateHstSyncSettings({
        accountId: selectedAccountId,
        active: robotActive,
        allowedIp: newValue,
      }).unwrap();
      refetchSettings();
      showToast("success", "Allowed IP saved");
    } catch (error) {
      console.error("Failed to update allowed IP:", error);
      showToast("error", "Could not save the allowed IP");
    }
  };

  // Settings are re-read too: the server auto-disables the robot after repeated failures, and
  // the robot switch should show that after a refresh.
  const handleRefresh = () => {
    refetchRecords();
    refetchStats();
    refetchSettings();
  };

  const stats = statsData?.data;
  const records = recordsData?.data || [];
  const totalRecords = recordsData?.total || 0;

  const handleStatusCardClick = (status: HstSyncStatus | null) => {
    setStatusFilter(status);
    setFirst(0);
  };

  const onPageChange = (event: PaginatorPageState) => {
    setFirst(event.first);
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDob = (dateStr: string): string => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const formatAmount = (amountInCents: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amountInCents / 100);
  };

  const patientNameTemplate = (row: IHstSyncRecord) => {
    const name = (
      <span className="sensitive" data-pii="patient-name">
        {row.patientFirstName} {row.patientLastName}
      </span>
    );
    // An estimate-ID search is not scoped to the selected hospital, so say which one the row is.
    if (!estimateIdFilter) return name;
    return (
      <div className="flex flex-column">
        {name}
        <MutedSmall>{row.account?.name}</MutedSmall>
      </div>
    );
  };

  const dobTemplate = (row: IHstSyncRecord) => {
    return <span className="sensitive">{formatDob(row.patientDob)}</span>;
  };

  const amountTemplate = (row: IHstSyncRecord) => {
    if (row.amountInCents === null || row.amountInCents === undefined) {
      return <span />;
    }
    return <span>{formatAmount(row.amountInCents)}</span>;
  };

  const clearPriceTemplate = (row: IHstSyncRecord) => {
    if (row.clearPriceInCents === null || row.clearPriceInCents === undefined) {
      return <span />;
    }
    return <span>{formatAmount(row.clearPriceInCents)}</span>;
  };

  const syncTypeTemplate = (row: IHstSyncRecord) => {
    return <TypeText>{TYPE_LABELS[row.syncType] ?? row.syncType}</TypeText>;
  };

  const noteTemplate = (row: IHstSyncRecord) => {
    if (!row.note) return <span>-</span>;

    const tooltipId = `note-tooltip-${row.id}`;

    return (
      <>
        <Tooltip target={`.${tooltipId}`} position="top" />
        <NoteText className={tooltipId} data-pr-tooltip={row.note}>
          {row.note}
        </NoteText>
      </>
    );
  };

  const statusTemplate = (row: IHstSyncRecord) => {
    return (
      <div className="flex flex-column align-items-start gap-1">
        <StatusBadge status={row.syncStatus} value={row.syncStatus} />
        {row.retryCount > 0 ? (
          <MutedSmall>retry {row.retryCount}</MutedSmall>
        ) : null}
      </div>
    );
  };

  const syncedAtTemplate = (row: IHstSyncRecord) => {
    if (row.syncStatus === "FAILED" && !row.syncedAt && row.failedAt) {
      return (
        <FailedAtText>
          <span className="failed-label">Failed</span>{" "}
          {formatDate(row.failedAt)}
        </FailedAtText>
      );
    }
    return <span>{formatDate(row.syncedAt)}</span>;
  };

  const errorTemplate = (row: IHstSyncRecord) => {
    if (!row.errorMessage) return <span>-</span>;

    const tooltipId = `error-tooltip-${row.id}`;

    return (
      <>
        <Tooltip target={`.${tooltipId}`} position="top" />
        <ErrorText className={tooltipId} data-pr-tooltip={row.errorMessage}>
          {row.errorMessage}
        </ErrorText>
      </>
    );
  };

  const handleUpdateStatus = async (
    e: React.MouseEvent,
    syncId: string,
    status: HstSyncStatus
  ) => {
    e.stopPropagation();
    try {
      await updateSyncStatus({ syncId, status }).unwrap();
      refetchRecords();
    } catch (error) {
      console.error("Failed to update sync status:", error);
      showToast("error", "Could not update the sync status");
    }
  };

  const actionsTemplate = (row: IHstSyncRecord) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-refresh"
          className="p-button-sm p-button-outlined p-button-secondary"
          tooltip="Re-sync"
          tooltipOptions={{ position: "top" }}
          aria-label="Re-sync"
          onClick={(e) => handleUpdateStatus(e, row.id, "PENDING")}
          disabled={row.syncStatus === "PENDING"}
        />
        <Button
          icon="pi pi-forward"
          className="p-button-sm p-button-outlined p-button-warning"
          tooltip="Skip"
          tooltipOptions={{ position: "top" }}
          aria-label="Skip"
          onClick={(e) => handleUpdateStatus(e, row.id, "SKIPPED")}
          disabled={row.syncStatus === "SKIPPED"}
        />
      </div>
    );
  };

  const handleRowClick = (row: IHstSyncRecord) => {
    if (row.orderId) {
      window.open(`/orders/${row.orderId}`, "_blank");
    }
  };

  const parsedRecords = useMemo(() => {
    return records.map((record) => ({
      ...record,
    }));
  }, [records]);

  if (statsLoading || settingsLoading) return <LoadingSpinner />;

  return (
    <div>
      {/* Hospital row: the selected hospital's robot settings, and Refresh */}
      <HospitalRow>
        {integrations.length > 0 ? (
          <Dropdown
            inputId="hst-sync-hospital"
            aria-label="Hospital"
            value={selectedAccountId}
            options={hospitalOptions}
            onChange={(e) => handleHospitalChange(e.value)}
            placeholder="Hospital"
            disabled={integrations.length <= 1}
            style={{ minWidth: "320px" }}
            panelStyle={{ minWidth: "320px" }}
          />
        ) : null}
        {selectedAccountId ? (
          <>
            <MonospaceInput>
              <InputText
                id="hst-sync-allowed-ip"
                aria-label="Allowed IP"
                value={allowedIpInput}
                onChange={(e) => setAllowedIpInput(e.target.value)}
                onBlur={handleAllowedIpBlur}
                placeholder="e.g. 44.196.216.234"
                style={{ width: "150px" }}
              />
            </MonospaceInput>
            <InputSwitch
              aria-label="Robot"
              checked={robotActive}
              onChange={(e) => handleRobotToggle(!!e.value)}
            />
            <span
              className={
                robotActive
                  ? "text-green-600 font-medium"
                  : "text-red-600 font-medium"
              }
            >
              {robotActive ? "Robot enabled" : "Robot disabled"}
            </span>
          </>
        ) : null}
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          className="p-button-outlined p-button-secondary ml-auto"
          onClick={handleRefresh}
          loading={recordsFetching || statsFetching}
        />
      </HospitalRow>

      {/* Filter row: the stat cards are the status filter (Total clears it), then search and type */}
      <ItemBar>
        <ItemBarLeft>
          <StatsCards>
            {STAT_CARDS.map((card) => {
              const active = statusFilter === card.status;
              return (
                <StatCard
                  key={card.key}
                  variant={card.variant}
                  clickable
                  active={active}
                  data-active={active}
                  data-testid={`hst-sync-stat-${card.key}`}
                  onClick={() => handleStatusCardClick(card.status)}
                >
                  <div className="stat-count">{stats?.[card.key] || 0}</div>
                  <div className="stat-label">{card.label}</div>
                </StatCard>
              );
            })}
          </StatsCards>
          <span
            className={
              searchInput
                ? "p-input-icon-left p-input-icon-right"
                : "p-input-icon-left"
            }
          >
            <i className="pi pi-search" />
            <InputText
              aria-label="Search"
              placeholder="Patient name, estimate ID or link…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ width: "340px" }}
            />
            {searchInput ? (
              <i
                className="pi pi-times cursor-pointer"
                onClick={clearSearch}
                onKeyDown={(e) => {
                  if (e.key === "Enter") clearSearch();
                }}
                role="button"
                tabIndex={0}
                aria-label="Clear search"
              />
            ) : null}
          </span>
          <Dropdown
            value={typeFilter}
            options={typeOptions}
            onChange={(e) => {
              setTypeFilter(e.value);
              setFirst(0);
            }}
            placeholder="Type"
            style={{ width: "180px" }}
          />
        </ItemBarLeft>
      </ItemBar>

      {/* Data Table */}
      {recordsLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <StyledDataTable
            value={parsedRecords}
            emptyMessage="No sync records found."
            stripedRows
            responsiveLayout="scroll"
            onRowClick={(e) => handleRowClick(e.data as IHstSyncRecord)}
            rowClassName={() => "cursor-pointer"}
          >
            <Column
              field="patientName"
              header="Patient Name"
              body={patientNameTemplate}
              headerStyle={{ width: "15%" }}
            />
            <Column
              field="patientDob"
              header="DOB"
              body={dobTemplate}
              headerStyle={{ width: "10%" }}
            />
            <Column
              field="accountNumber"
              header="Account #"
              headerStyle={{ width: "10%" }}
            />
            <Column
              field="clearPriceInCents"
              header="Clear Price"
              body={clearPriceTemplate}
              headerStyle={{ width: "10%" }}
            />
            <Column
              field="amountInCents"
              header="Payment Amount"
              body={amountTemplate}
              headerStyle={{ width: "10%" }}
            />
            <Column
              field="syncType"
              header="Type"
              body={syncTypeTemplate}
              headerStyle={{ width: "12%" }}
            />
            <Column
              field="note"
              header="Note"
              body={noteTemplate}
              headerStyle={{ width: "12%" }}
            />
            <Column
              field="syncStatus"
              header="Status"
              body={statusTemplate}
              headerStyle={{ width: "8%" }}
            />
            <Column
              field="syncedAt"
              header="Synced At"
              body={syncedAtTemplate}
              headerStyle={{ width: "12%" }}
            />
            <Column
              field="errorMessage"
              header="Error"
              body={errorTemplate}
              headerStyle={{ width: "14%" }}
            />
            <Column
              header="Actions"
              body={actionsTemplate}
              headerStyle={{ width: "10%" }}
            />
          </StyledDataTable>
          <Paginator
            first={first}
            rows={PAGE_SIZE}
            totalRecords={totalRecords}
            onPageChange={onPageChange}
          />
        </>
      )}
    </div>
  );
}
